import { Avatar } from "@base-ui/react";
import Image from "next/image";
import Link from "next/link";
import type { Dispatch, SetStateAction } from "react";
import verifiedBadge from "#/assets/badge/verified-badge.svg";
import { ContentDate } from "@/components/content/interactions";
import { LoginRequiredDialog } from "@/components/content/interactions/login-required-dialog";
import BookmarkIcon from "@/components/icons/bookmark";
import LikeIcon from "@/components/icons/like";
import MessageIcon from "@/components/icons/message";
import ShareIcon from "@/components/icons/share";

export interface SearchResult {
  id: string;
  title: string;
  slug: string;
  lead?: string;
  summary?: string;
  thumbnailUrl?: string;
  coverImageUrl?: string;
  publishedAt?: string;
  likeCount?: number;
  commentCount?: number;
  isLiked?: boolean;
  isSaved?: boolean;
  author?: {
    name: string;
    avatar?: string;
    title?: string;
    isVerified?: boolean;
  };
  categoryNames?: string[];
  publishedLabel?: string;
}

interface SearchItemsProps {
  results: SearchResult[];
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export function SearchItems({ results, setIsOpen }: SearchItemsProps) {
  if (results.length === 0) {
    return (
      <div className="text-center py-12 text-neutral-500">
        Sonuç bulunamadı.
      </div>
    );
  }

  return (
    <>
      {results.map((result) => (
        <div
          className="flex flex-col md:flex-row gap-6 md:gap-8 py-8 border-b border-neutral-100 last:border-none"
          key={result.id}
        >
          <div className="flex-1 flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <Avatar.Root className="inline-flex size-10 items-center justify-center overflow-hidden rounded-full bg-gray-100 align-middle text-sm font-medium text-gray-900 select-none shrink-0">
                {result.author?.avatar && (
                  <Avatar.Image
                    alt={result.author.name}
                    className="size-full object-cover"
                    src={result.author.avatar}
                  />
                )}
                <Avatar.Fallback className="flex size-full items-center justify-center text-sm font-bold uppercase text-neutral-500">
                  {result.author?.name?.charAt(0) || "U"}
                </Avatar.Fallback>
              </Avatar.Root>
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  <span className="text-sm font-bold text-neutral-800">
                    {result.author?.name}
                  </span>
                  {result.author?.isVerified && (
                    <Image
                      alt="Doğrulanmış Hesap"
                      className="size-4"
                      src={verifiedBadge}
                    />
                  )}
                </div>
                <span className="text-xs text-neutral-500">
                  {result.author?.title}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 mb-3">
              <span className="text-[11px] text-neutral-400 font-medium">
                {result.categoryNames?.[0] || ""}
              </span>
              <Link href={`/${result.slug}`} onClick={() => setIsOpen(false)}>
                <h3 className="text-[22px] font-bold text-neutral-800 hover:text-brand-blue-500 transition-colors leading-snug">
                  {result.title}
                </h3>
              </Link>
            </div>

            <Link
              className="text-sm text-neutral-600 leading-relaxed line-clamp-2 mb-6 md:mb-8"
              href={`/${result.slug}`}
              onClick={() => setIsOpen(false)}
            >
              {result.lead || result.summary || ""}
            </Link>

            <div className="flex items-center justify-between mt-auto">
              <div className="flex items-center gap-5 text-neutral-400 text-sm font-medium">
                {result.publishedAt && (
                  <ContentDate publishedAt={result.publishedAt} />
                )}
                <Link
                  className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                  href={`/${result.slug}`}
                  onClick={() => setIsOpen(false)}
                >
                  <LikeIcon
                    className={
                      result.isLiked
                        ? "size-5 stroke-[1.5] stroke-brand-blue-500 fill-brand-blue-100"
                        : "size-5 stroke-[1.5] stroke-neutral-400 fill-none"
                    }
                  />
                  <span>{result.likeCount || 0}</span>
                </Link>
                <Link
                  className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                  href={`/${result.slug}`}
                  onClick={() => setIsOpen(false)}
                >
                  <MessageIcon className="size-5 stroke-[1.5] stroke-neutral-400 fill-none" />
                  <span>{result.commentCount || 0}</span>
                </Link>
              </div>
              <div className="flex items-center gap-4 text-brand-blue-500">
                <button
                  aria-label="Paylaş"
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                  type="button"
                >
                  <ShareIcon className="size-5 stroke-[1.5] stroke-neutral-400 hover:stroke-brand-blue-500 fill-none" />
                </button>
                <LoginRequiredDialog action="favorite" />
              </div>
            </div>
          </div>

          <Link
            className="shrink-0 group mt-4 md:mt-0"
            href={`/${result.slug}`}
            onClick={() => setIsOpen(false)}
          >
            <div className="relative w-full md:w-[320px] h-[200px] rounded-2xl overflow-hidden bg-neutral-100">
              <Image
                alt={result.title}
                className="w-full h-full object-cover"
                height={200}
                src={result.thumbnailUrl || result.coverImageUrl || ""}
                width={320}
              />
            </div>
          </Link>
        </div>
      ))}
    </>
  );
}
